import React, { useState } from 'react';
import { Head, Link, router, useForm } from '@inertiajs/react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

import { ArrowLeft, Plus, Edit, Trash2, Filter, RefreshCw, Download, FileText } from 'lucide-react';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';

interface Document {
  id: number;
  name: string;
}

interface ExtractJob {
  id: number;
  job_id: string;
  name?: string;
}

interface Divisor {
  id: number;
  divisor_type: string;
  title: string;
  content_preview: string;
  parent_id?: number;
  children?: Divisor[];
}

interface DivisorsIndexProps {
  document: Document;
  extractJob: ExtractJob;
  divisors: Divisor[];
  divisorTypes: string[];
  currentFilter?: string;
}

export default function DivisorsIndex({ 
  document, 
  extractJob, 
  divisors, 
  divisorTypes,
  currentFilter 
}: DivisorsIndexProps) {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [filterType, setFilterType] = useState(currentFilter || 'all');

  const { delete: destroy, processing: deleting } = useForm();
  const { post, processing: updating } = useForm();

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(divisors.map(d => d.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    }
  };

  const handleUpdateExtractResponse = () => {
    if (confirm('This will update the extract response payload with the current divisors. Continue?')) {
      post(`/documents/${document.id}/extract_jobs/${extractJob.id}/divisors/update_extract_response`);
    }
  };

  const handleCreateFromJsonMap = () => {
    router.get(`/documents/${document.id}/extract_jobs/${extractJob.id}/divisors/create_from_json_map`);
  };

  const handleCreateFromExtract = () => {
    if (confirm('This will create divisors from the extract response. Any existing divisors may be affected. Continue?')) {
      post(`/documents/${document.id}/extract_jobs/${extractJob.id}/divisors/create_from_extract`);
    }
  };

  const handleDeleteMultiple = () => {
    if (selectedIds.length === 0) return;
    
    if (confirm(`Are you sure you want to delete ${selectedIds.length} divisor(s)?`)) {
      router.delete(`/documents/${document.id}/extract_jobs/${extractJob.id}/divisors/delete_multiple`, {
        data: { divisor_ids: selectedIds },
        onSuccess: () => setSelectedIds([])
      });
    }
  };

  const handleFilterChange = (value: string) => {
    setFilterType(value);
    const url = `/documents/${document.id}/extract_jobs/${extractJob.id}/divisors`;
    router.get(url, { divisor_type: value === 'all' ? undefined : value });
  };

  const renderDivisorTree = (divisors: Divisor[], level = 0) => {
    return divisors.map((divisor) => (
      <React.Fragment key={divisor.id}>
        <TableRow>
          <TableCell className="w-12">
            <Checkbox
              checked={selectedIds.includes(divisor.id)}
              onCheckedChange={(checked) => handleSelectOne(divisor.id, checked as boolean)}
            />
          </TableCell>
          <TableCell>
            <div className={`pl-${level * 4}`}>
              <div className="font-semibold">{divisor.title}</div>
              <div className="text-sm text-gray-600">{divisor.divisor_type}</div>
            </div>
          </TableCell>
          <TableCell>
            <div className="text-sm max-w-xs truncate">
              {divisor.content_preview}
            </div>
          </TableCell>
          <TableCell>
            <div className="flex gap-2">
              <Button asChild size="sm" variant="outline">
                <Link href={`/documents/${document.id}/extract_jobs/${extractJob.id}/divisors/${divisor.id}/edit`}>
                  <Edit className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => {
                  if (confirm('Are you sure you want to delete this divisor?')) {
                    router.delete(`/documents/${document.id}/extract_jobs/${extractJob.id}/divisors/${divisor.id}`);
                  }
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </TableCell>
        </TableRow>
        {divisor.children && divisor.children.length > 0 && 
          renderDivisorTree(divisor.children, level + 1)
        }
      </React.Fragment>
    ));
  };

  return (
    <>
      <Head title={`Divisors for ${document.name}`} />
      
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="space-y-6">
          {/* Breadcrumbs */}
          <Breadcrumbs opsConsole
            items={[
              { label: document.name, href: `/documents/${document.id}` },
              { label: 'Extract Jobs', href: `/documents/${document.id}/extract_jobs/${extractJob.id}/edit` },
              { label: 'Divisors', current: true }
            ]}
          />

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold">Divisors for "{document.name}"</h1>
              <p className="text-gray-600">Job: {extractJob.job_id}</p>
            </div>
            
            <div className="flex gap-3">
              <Button asChild variant="outline">
                <Link href={`/documents/${document.id}`}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Document
                </Link>
              </Button>
              <Button 
                variant="secondary"
                onClick={handleCreateFromJsonMap}
                disabled={updating}
              >
                <FileText className="h-4 w-4 mr-2" />
                Create from JSON Map
              </Button>
              <Button 
                variant="secondary"
                onClick={handleCreateFromExtract}
                disabled={updating}
              >
                <Download className="h-4 w-4 mr-2" />
                Create from Extract
              </Button>
              <Button asChild>
                <Link href={`/documents/${document.id}/extract_jobs/${extractJob.id}/divisors/new`}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Divisor
                </Link>
              </Button>
            </div>
          </div>

          {/* Controls */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                {/* Filter */}
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  <Select value={filterType} onValueChange={handleFilterChange}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter by type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {divisorTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  {/* Update Extract Response */}
                  <Button
                    variant="outline"
                    onClick={handleUpdateExtractResponse}
                    disabled={updating}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Update Extract Response
                  </Button>

                  {/* Delete Selected */}
                  <Button
                    variant="destructive"
                    disabled={selectedIds.length === 0 || deleting}
                    onClick={handleDeleteMultiple}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected ({selectedIds.length})
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Divisors Table */}
          <Card>
            <CardHeader>
              <CardTitle>Divisors ({divisors.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {divisors.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedIds.length === divisors.length && divisors.length > 0}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Title & Type</TableHead>
                      <TableHead>Content Preview</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {renderDivisorTree(divisors)}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No divisors found.</p>
                  <Button asChild className="mt-4">
                    <Link href={`/documents/${document.id}/extract_jobs/${extractJob.id}/divisors/new`}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Divisor
                    </Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}